import React, { useEffect, useState } from "react";
import { useTable, useSortBy } from "react-table";
import Skeleton from "@mui/material/Skeleton";
import { useTranslation } from "react-i18next";
import { detectWebDeviceType } from "../../utils";

function CreditTableComponent({
    columns,
    data,
    pagination, // object: { current, pageSize, onChange }
    onRowClick,
    selectedRowIndex,
    isLoadingData,
}) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(true);
    const deviceType = detectWebDeviceType();

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        rows,
    } = useTable(
        {
            columns,
            data,
        },
        useSortBy
    );

    useEffect(() => {
        if (data.length > 0) {
            setIsLoading(false);
        } else if (isLoadingData) {
            setIsLoading(true);
        }
    }, [data, isLoadingData]);

    if (isLoading) {
        return (
            <div className="p-4">
                {[...Array(6)].map((_, index) => (
                    <Skeleton
                        key={index}
                        variant="rectangular"
                        width="100%"
                        height={index % 2 === 0 ? 40 : 60}
                        className="mb-3"
                    />
                ))}
            </div>
        );
    }

    return (
        <>
            {data.length > 0 && (
                <table {...getTableProps()} className="w-full">
                    <thead className="bg-[#C9ECF7] text-zinc-800 font-semibold text-lg text-left capitalize whitespace-nowrap">
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps(column.getSortByToggleProps())}
                                        key={column.id}
                                        className="px-6 py-3 text-center"
                                    >
                                        {column.render("Header")}
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>

                    <tbody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr
                                    {...row.getRowProps()}
                                    key={row.id}
                                    onClick={() => onRowClick(row.original)}
                                    className={`cursor-pointer ${selectedRowIndex === i ? "bg-gray-200" : ""
                                        } border-b border-gray-300`}
                                >
                                    {row.cells.map((cell) => (
                                        <td
                                            {...cell.getCellProps()}
                                            key={cell.column.id}
                                            className={`px-6 ${deviceType === "tablet" ? "py-2" : "py-3"} text-center`}
                                        >
                                            <div className={`${deviceType === "tablet" ? "text-base" : "text-lg"} text-gray-900`}>
                                                {cell.render("Cell")}
                                            </div>
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {/* ✅ Pagination Footer */}
            <footer className="flex justify-center text-lg self-center px-5 mt-2 w-full">
                <div className="flex gap-5 text-center text-black font-semibold w-2/12">
                    <button
                        onClick={() => pagination.onChange(pagination.current - 1)}
                        disabled={pagination.current === 1 || isLoadingData}
                        className="p-2 rounded-full bg-gray-200"
                        style={{ width: "40px", height: "40px" }}
                    >
                        &lt;
                    </button>
                    <div className="flex-auto my-auto">
                        {t("Results")}{" "}
                        <strong>
                            {(pagination.current - 1) * pagination.pageSize + 1} -{" "}
                            {(pagination.current - 1) * pagination.pageSize + data.length}
                        </strong>
                    </div>
                    <button
                        onClick={() => pagination.onChange(pagination.current + 1)}
                        disabled={data.length < pagination.pageSize || isLoadingData}
                        className="p-2 rounded-full bg-gray-200"
                        style={{ width: "40px", height: "40px" }}
                    >
                        &gt;
                    </button>
                </div>
            </footer>
        </>
    );
}

export default CreditTableComponent;